import { useTranslation } from "react-i18next";
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  Toggle,
} from "@carbon/react";

export interface MaterialFileUploadProps {
  /** Name of the file the user has picked, or `null` before any pick — mirrors the pre-Carbon
   * form's own `fileName` state, still owned by the parent (`NewMaterialForm.tsx`) so its
   * `resetFormState`/"create another" flow can clear it the same way it always did. */
  fileName: string | null;
  isUploading: boolean;
  /** Called with the raw `File` the moment one is picked — the parent immediately hands it to
   * `useFileInputUploader`'s `mutate`, same as the pre-Carbon form's `handleSpecification`. */
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
  /** The pre-Carbon form rendered "Confidential Specification" directly beneath this same
   * upload control (`newMaterialForm.confidential`) — kept here rather than split into a sibling
   * field so the two stay visually and logically paired, exactly as before. Not an RHF field in
   * either version: `newMaterialValidationSchema` never had a rule for it. */
  isSpecConfidential: boolean;
  onSpecConfidentialChange: (confidential: boolean) => void;
}

/**
 * The specification-file picker (Task 6's first use of Carbon's `FileUploader` family in this
 * migration) — bound to the SAME `useFileInputUploader` hook the pre-Carbon `NewMaterialForm`
 * used, only the widget changed. No Yup rule ever governed this field (see
 * `newMaterialFormValidationSchema.ts`'s own docstring on why `specifications` isn't in the
 * schema) — this component owns none of its own validation, it only reports a pick upward.
 *
 * `FileUploaderDropContainer` stays visible even after a file is picked, exactly like the
 * pre-Carbon control (a plain `<input type="file">` that was always clickable): the user can
 * pick a replacement at any time and it overwrites the current selection, the same "just pick
 * again" affordance as before. The one addition is `FileUploaderItem`'s own delete (X) button —
 * the pre-Carbon form had no way to clear a selection short of picking a different file; this is
 * a small, non-load-bearing UX improvement Carbon's own component provides for free, not a
 * validation-parity concern (there was never a rule requiring a file in the first place).
 *
 * `accept={[".pdf"]}` narrows the OS file picker's own filter to match the "PDF 10MB" hint text
 * the pre-Carbon screen displayed — cosmetic only. Deliberately NOT setting `maxFileSize`: the
 * pre-Carbon input enforced no real size limit (the "10MB" text was decorative), and adding a
 * hard block here would be a new, blocking rule this task isn't scoped to introduce.
 */
const MaterialFileUpload = ({
  fileName,
  isUploading,
  onFileSelected,
  onFileRemoved,
  isSpecConfidential,
  onSpecConfidentialChange,
}: MaterialFileUploadProps) => {
  const { t } = useTranslation();

  return (
    <div className="material-file-upload">
      <p className="material-file-upload__label cds--label">
        {t("newMaterialForm.specification")}
      </p>
      <FileUploaderDropContainer
        accept={[".pdf"]}
        labelText={
          isUploading ? t("cta.uploading") : t("newMaterialForm.upload")
        }
        onAddFiles={(_event, { addedFiles }) => {
          const file = addedFiles[0];
          if (file) {
            onFileSelected(file);
          }
        }}
      />
      {fileName ? (
        <FileUploaderItem
          name={fileName}
          status={isUploading ? "uploading" : "complete"}
          onDelete={onFileRemoved}
        />
      ) : null}
      <p className="material-file-upload__hint">PDF 10MB</p>
      <Toggle
        id="confidentSpec"
        labelText={t("newMaterialForm.confidential")}
        toggled={isSpecConfidential}
        onToggle={onSpecConfidentialChange}
      />
    </div>
  );
};

export default MaterialFileUpload;
